import { useMutation, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto } from "@/api/cliente"
/**
 * Relés e fusíveis — projeto, caixa, e o que cada posição alimenta.
 *
 * Três níveis, e a hierarquia é o caminho de quem procura: acho o **projeto**
 * (uma família de veículos), acho a **caixa** (a central de um modelo), vejo os
 * **itens** (F19, R3 — o que cada posição faz).
 *
 * **A mesma caixa física existe uma vez por modelo**, e é aí que o volume mora:
 * 401 projetos guardam 7.117 caixas e 25.863 itens. Um projeto chega a **504
 * caixas** com uma média de 19 — a lista de caixas agrupa por título, senão são
 * quinhentas linhas quase iguais.
 *
 * Não confundir com as **Caixas de fusíveis** da aba de projeto: aquelas são do
 * módulo de pinout e pertencem a um projeto de pinout. Estas são conteúdo
 * publicado, dado global.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  type CaixaDeReles, type ItemDeReles, type ProjetoDeReles, TETO_DO_ITEM_DE_RELES, usarFilhos, usarGravar, usarIncluir, usarMestre, usarPublicacao, usarSubirImagem,
} from "@/api/outrosProjetos"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { CaixasEmLote } from "./CaixasEmLote"
import { LoteDePosicoes } from "./LoteDePosicoes"
import { ordenarLista, usarOrdemDaLista, usarRecemIncluido } from "@/kit/ordemDaLista"
import { casaBusca } from "@/kit/buscaDeTexto"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { ConfirmacaoDigitada } from "@/kit/ConfirmacaoDigitada"
import { ErroDaApi } from "@/api/erros"
import { Galeria, usarImagem, type CarregarImagem } from "@/telas/feedback/FeedbackDaEquipe"
import { MenuDeAcoes } from "@/kit/MenuDeAcoes"
import {
  Botao, Campo, ColunaRecolhivel, EstadoDePublicacao, Etiqueta, FiltroDaLista, ItemDaLista, Janela, Linha, Painel, Secao, TabelaSimples, Vazio, estiloCampo, mensagem, nomeOu, usarNomes,
} from "./pecas"
import { lerPosicoes } from "./CaixasEmLote"

export function RelesFusiveis() {
  const [busca, setBusca] = useState("")
  const [projeto, setProjeto] = useState<ProjetoDeReles | null>(null)
  const [caixa, setCaixa] = useState<CaixaDeReles | null>(null)
  const [incluindo, setIncluindo] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)

  const nomes = usarNomes()
  const lista = usarMestre<ProjetoDeReles>("reles", "api/reles-fusiveis")

  // "Mais recentes primeiro" é a ordem em que a API já manda: o último criado ou alterado
  // (o projeto ou algo dentro dele) no topo. A alfabética é ordenada aqui.
  const [ordem, setOrdem] = usarOrdemDaLista("reles-projetos")

  const achados = useMemo(() => {
    const filtrados = busca.trim() ? lista.itens.filter((p) => casaBusca(busca, p.titulo)) : lista.itens
    return ordem === "alfabetica"
      ? ordenarLista(filtrados, "alfabetica", (p) => p.id, (p) => p.titulo ?? "")
      : filtrados
  }, [lista.itens, busca, ordem])

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa titulo="Relés e fusíveis" detalhe={`${lista.total} projetos`}>
        {/* Abrir a inclusão não mexe na seleção: cancelar volta para a tela como estava. */}
        <Botao destaque aoClicar={() => { setRecado(null); setIncluindo(true) }}>
          Incluir projeto
        </Botao>
      </Faixa>

      <AvisoDeDadoGlobal oQueMuda="Estes projetos são conteúdo publicado, não projeto de pinout" />

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <ColunaRecolhivel chave="reles-projetos" titulo="Projetos" largura={300}
                          detalhe={busca.trim() ? `${achados.length} de ${lista.total}` : `${lista.total}`}
                          topo={
                            // Filtro e ordem moram com a lista que eles mudam, não na faixa do topo.
                            <FiltroDaLista valor={busca} aoMudar={setBusca} rotulo="Filtrar projetos"
                                           ordem={ordem} aoMudarOrdem={setOrdem} />
                          }>
          {lista.isPending ? (
            <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
          ) : lista.error ? (
            <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
              {lista.error instanceof ErroDaApi ? lista.error.message : "Não foi possível ler."}
            </p>
          ) : achados.length === 0 ? (
            <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Nada com esse texto.</p>
          ) : (
            achados.map((p) => (
              <ItemDaLista
                key={p.id}
                ativo={projeto?.id === p.id}
                marcado={p.publicado}
                aoClicar={() => { setProjeto(p); setCaixa(null) }}
                titulo={p.titulo || `Projeto ${p.id}`}
                detalhe={p.alteradoEm ? `alterado em ${dataCurta(p.alteradoEm)}` : undefined}
              />
            ))
          )}
        </ColunaRecolhivel>

        {projeto ? (
          <DoProjeto
            projeto={projeto}
            caixa={caixa}
            aoEscolherCaixa={setCaixa}
            nomes={nomes}
            aoAvisar={setRecado}
            aoApagar={() => { setProjeto(null); setCaixa(null) }}
          />
        ) : (
          <Vazio>
            Escolha um projeto à esquerda. São 401, e cada um reúne as caixas de uma família de
            veículos — o maior tem 504 caixas, uma por modelo.
          </Vazio>
        )}
      </div>

      {incluindo && (
        <Inclusao
          aoAvisar={setRecado}
          aoFechar={() => setIncluindo(false)}
          aoCriar={(p) => { setIncluindo(false); setProjeto(p); setCaixa(null) }}
        />
      )}

      <BarraDeEstado>{recado ?? ""}</BarraDeEstado>
    </div>
  )
}

/** "16/10/2026 14:05". A API manda hora local sem fuso, e é assim que se lê. */
function dataCurta(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
}

/**
 * Incluir projeto de relés e fusíveis.
 *
 * Um campo só, e é o que a API pede: `POST api/reles-fusiveis` exige o título e nada
 * mais. As caixas vêm depois, e para elas já existe a inclusão em lote na coluna do
 * projeto.
 */
function Inclusao({ aoAvisar, aoFechar, aoCriar }: {
  aoAvisar: (m: string | null) => void
  aoFechar: () => void
  aoCriar: (p: ProjetoDeReles) => void
}) {
  const incluir = usarIncluir<ProjetoDeReles>([["outros", "reles"]])
  const [titulo, setTitulo] = useState("")

  const gravar = () => {
    if (!titulo.trim()) return
    aoAvisar(null)
    incluir.mutate({ rota: "api/reles-fusiveis", dados: { titulo: titulo.trim() } }, {
      onSuccess: (p) => {
        aoAvisar(`Projeto ${p.id} criado, fora do ar. Acrescente as caixas.`)
        aoCriar(p)
      },
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  return (
    <Janela titulo="Incluir projeto de relés e fusíveis" aoFechar={aoFechar}>
      <Secao titulo="O projeto">
        <Campo rotulo="Título" ajuda="Reúne as caixas de uma família de veículos.">
          <input autoFocus value={titulo} style={estiloCampo}
                 onKeyDown={(e) => { if (e.key === "Enter") gravar() }}
                 onChange={(e) => setTitulo(e.target.value)} />
        </Campo>

        <p style={{ margin: "2px 0 10px", fontSize: 11, color: "var(--txt3)", lineHeight: 1.5,
                    maxWidth: "70ch" }}>
          Nasce <b>fora do ar</b>. Depois de criar, a coluna das caixas abre — e a inclusão
          em lote é o caminho quando a mesma central existe em vários modelos.
        </p>

        <div style={{ display: "flex", gap: 6 }}>
          <Botao destaque ocupado={incluir.isPending} desligado={!titulo.trim()} aoClicar={gravar}>
            {incluir.isPending ? "Criando…" : "Criar o projeto"}
          </Botao>
          <Botao aoClicar={aoFechar}>Cancelar</Botao>
        </div>
      </Secao>
    </Janela>
  )
}

function DoProjeto({ projeto, caixa, aoEscolherCaixa, nomes, aoAvisar, aoApagar }: {
  projeto: ProjetoDeReles
  caixa: CaixaDeReles | null
  aoEscolherCaixa: (c: CaixaDeReles | null) => void
  nomes: ReturnType<typeof usarNomes>
  aoAvisar: (m: string | null) => void
  aoApagar: () => void
}) {
  const base = `api/reles-fusiveis/${projeto.id}`
  const caixas = usarFilhos<CaixaDeReles>(["reles-caixas", projeto.id], `${base}/caixas`)

  // Publicar e do PROJETO, nao da caixa: e o projeto inteiro que aparece ou
  // some para quem paga.
  // A chave e a do `usarMestre("reles", ...)`: invalidar a lista e o que faz o
  // marcador da coluna da esquerda acompanhar o botao.
  const publicacao = usarPublicacao([["outros", "reles"]])

  // Renomear e excluir o PROJETO. A API tem `PUT` e `DELETE` desde sempre; esta tela não
  // tinha nenhum dos dois, então um título errado era permanente e um projeto criado por
  // engano ficava para sempre.
  const { alterar, excluir } = usarGravar([["outros", "reles"]])
  const [titulo, setTitulo] = useState(projeto.titulo ?? "")
  useEffect(() => setTitulo(projeto.titulo ?? ""), [projeto])

  // Excluir pede o título digitado. Era um clique ao lado do "gravar", e foi assim que um
  // projeto inteiro sumiu de produção em 20/10/2026 — a tela não tem como desfazer.
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false)
  useEffect(() => setConfirmandoExclusao(false), [projeto.id])
  const nomeDoProjeto = projeto.titulo?.trim() || `Projeto ${projeto.id}`
  const quantasCaixas = caixas.data?.length ?? 0

  /** Agrupado por título: 504 caixas viram poucos grupos, um por central. */
  const grupos = useMemo(() => {
    const mapa = new Map<string, CaixaDeReles[]>()
    for (const c of caixas.data ?? []) {
      const t = (c.titulo ?? "").trim() || "(sem título)"
      mapa.set(t, [...(mapa.get(t) ?? []), c])
    }
    return [...mapa.entries()].sort((a, b) => b[1].length - a[1].length)
  }, [caixas.data])

  const [abertos, setAbertos] = useState<Set<string>>(new Set())

  // A caixa escolhida relida da lista: trocar as posições dela (PUT) invalida a lista, e a
  // cópia guardada no clique ficaria com as posições velhas.
  const caixaAtual = (caixa && caixas.data?.find((c) => c.id === caixa.id)) || caixa

  return (
    <>
      <ColunaRecolhivel chave="reles-caixas" titulo="Caixas" largura={330}
                        detalhe={projeto.titulo || `Projeto ${projeto.id}`}>
        <EstadoDePublicacao
          publicado={projeto.publicado}
          oQue="este projeto"
          ocupado={publicacao.isPending}
          aoTrocar={(publicar) => {
            aoAvisar(null)
            publicacao.mutate({ base, publicar }, {
              onSuccess: () => aoAvisar(publicar ? "Projeto no ar." : "Projeto fora do ar."),
              onError: (e) => aoAvisar(mensagem(e)),
            })
          }}
        />
        <div style={{ display: "flex", gap: 5, alignItems: "center", margin: "8px 0" }}>
          <input
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            aria-label="Título do projeto"
            style={{ ...estiloCampo, flex: 1, minWidth: 0 }}
          />
          <Botao
            ocupado={alterar.isPending}
            desligado={!titulo.trim() || titulo === projeto.titulo}
            aoClicar={() => {
              aoAvisar(null)
              alterar.mutate({ rota: base, dados: { ...projeto, titulo: titulo.trim() } }, {
                onSuccess: () => aoAvisar("Título gravado."),
                onError: (e) => aoAvisar(mensagem(e)),
              })
            }}
          >
            Gravar
          </Botao>
          <Botao
            desligado={confirmandoExclusao}
            aoClicar={() => { aoAvisar(null); setConfirmandoExclusao(true) }}
          >
            Excluir
          </Botao>
        </div>

        {confirmandoExclusao && (
          <div style={{ marginBottom: 8 }}>
            <ConfirmacaoDigitada
              alvo={nomeDoProjeto}
              oQueAcontece={
                <>
                  Exclui o projeto <b>{nomeDoProjeto}</b>
                  {quantasCaixas > 0 && <>, com as suas <b>{quantasCaixas} caixas</b></>}. Ele sai da
                  lista e <b>não há como desfazer pela tela</b>.
                </>
              }
              rotuloDoBotao="Excluir o projeto"
              ocupado={excluir.isPending}
              aoCancelar={() => setConfirmandoExclusao(false)}
              aoConfirmar={() => {
                aoAvisar(null)
                excluir.mutate(base, {
                  onSuccess: () => { aoAvisar(`Projeto ${projeto.id} excluído.`); aoApagar() },
                  onError: (e) => { aoAvisar(mensagem(e)); setConfirmandoExclusao(false) },
                })
              }}
            />
          </div>
        )}

        <CaixasEmLote
          projetoId={projeto.id}
          nomes={nomes}
          invalidar={[["outros-filhos", "reles-caixas", projeto.id], ["outros", "reles"]]}
          aoAvisar={aoAvisar}
        />

        <p style={{ margin: "8px 0", fontSize: 11, color: "var(--txt3)", lineHeight: 1.5 }}>
          <b>{caixas.data?.length ?? 0} caixas</b> em {grupos.length} {grupos.length === 1 ? "central" : "centrais"}.
          A mesma central existe uma vez por modelo — por isso agrupadas.
        </p>

        {caixas.isPending ? (
          <p style={{ fontSize: 12, color: "var(--txt2)" }}>Carregando as caixas…</p>
        ) : grupos.length === 0 ? (
          <p style={{ fontSize: 12, color: "var(--txt3)" }}>Este projeto não tem caixa.</p>
        ) : (
          grupos.map(([titulo, doGrupo]) => {
            const aberto = abertos.has(titulo)
            return (
              <div key={titulo} style={{ marginBottom: 5 }}>
                <button
                  onClick={() => setAbertos((s) => {
                    const n = new Set(s)
                    n.has(titulo) ? n.delete(titulo) : n.add(titulo)
                    return n
                  })}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    border: "1px solid var(--line2)", borderRadius: 6, padding: "6px 9px",
                    cursor: "pointer", background: "var(--surface)", fontSize: 12,
                  }}
                >
                  {aberto ? "▾" : "▸"} {titulo}
                  <span style={{ float: "right", color: "var(--txt3)", fontSize: 10.5 }}>
                    {doGrupo.length}
                  </span>
                </button>

                {aberto && doGrupo.slice(0, 200).map((c) => (
                  <button
                    key={c.id}
                    onClick={() => aoEscolherCaixa(c)}
                    style={{
                      display: "block", width: "calc(100% - 14px)", marginLeft: 14, textAlign: "left",
                      border: 0, borderRadius: 4, padding: "3px 7px", cursor: "pointer",
                      fontSize: 11, marginTop: 2,
                      background: caixa?.id === c.id ? "var(--acao-soft)" : "transparent",
                      color: caixa?.id === c.id ? "var(--acao-on-soft)" : "var(--txt2)",
                    }}
                  >
                    {nomeOu(nomes.veiculo, c.veiculoId, "veículo")}
                    {c.ano ? ` · ${c.ano}` : ""}
                  </button>
                ))}
                {aberto && doGrupo.length > 200 && (
                  <p style={{ margin: "3px 0 0 14px", fontSize: 10, color: "var(--txt3)" }}>
                    {doGrupo.length} caixas; mostrando 200.
                  </p>
                )}
              </div>
            )
          })
        )}
      </ColunaRecolhivel>

      {caixaAtual ? (
        <DaCaixa key={caixaAtual.id} projeto={projeto} caixa={caixaAtual} nomes={nomes} aoAvisar={aoAvisar}
                 aoFechar={() => aoEscolherCaixa(null)} />
      ) : (
        <Vazio>Abra uma central e escolha a caixa do modelo para ver o que cada posição alimenta.</Vazio>
      )}
    </>
  )
}

function DaCaixa({ projeto, caixa, nomes, aoAvisar, aoFechar }: {
  projeto: ProjetoDeReles
  caixa: CaixaDeReles
  nomes: ReturnType<typeof usarNomes>
  aoAvisar: (m: string | null) => void
  aoFechar: () => void
}) {
  const base = `api/reles-fusiveis/${projeto.id}/caixas/${caixa.id}`
  const itens = usarFilhos<ItemDeReles>(["reles-itens", caixa.id], `${base}/itens`)
  const imagens = usarFilhos<ImagemDaCaixa>(["reles-imagens", caixa.id], `${base}/imagens`)

  const posicoes = usarFilhos<string>(["reles-identificadores", caixa.id], `${base}/identificadores`)

  // Incluir ou alterar linha pode pôr posição nova na caixa (a API faz isso para a linha
  // não sumir de quem a digitou): as posições e a lista de caixas também se renovam.
  const invalidar = [["outros-filhos", "reles-itens", caixa.id],
                     ["outros-filhos", "reles-imagens", caixa.id],
                     ["outros-filhos", "reles-identificadores", caixa.id],
                     ["outros-filhos", "reles-caixas", projeto.id],
                     // Mexer na caixa marca o projeto como alterado: a lista reordena.
                     ["outros", "reles"]]
  const { criar, alterar, excluir } = usarGravar(invalidar)
  const subir = usarSubirImagem(invalidar)

  const [novo, setNovo] = useState<Posicao>(POSICAO_VAZIA)
  const designacaoNova = useRef<HTMLInputElement>(null)
  const [emLote, setEmLote] = useState(false)

  // O que acabou de entrar fica realçado. Só o lote rola a tela até as linhas novas: na
  // inclusão uma a uma, rolar tiraria o formulário da frente de quem está digitando.
  const recente = usarRecemIncluido()
  const tabela = useRef<HTMLDivElement>(null)
  const rolarAteNovas = useRef(false)
  useEffect(() => {
    if (!rolarAteNovas.current || recente.id == null) return
    const alvo = tabela.current?.querySelector<HTMLElement>("[data-recem-incluido]")
    if (!alvo) return
    rolarAteNovas.current = false
    alvo.scrollIntoView?.({ block: "center", behavior: "smooth" })
  }, [recente.id, itens.data])

  // A linha clicada, em edição. Corrigir era apagar e incluir de novo — e a linha
  // corrigida ia parar no fim da tabela.
  const [aberto, setAberto] = useState<ItemDeReles | null>(null)
  const edicao = useRef<HTMLDivElement>(null)

  // A linha que o × marcou para remover. Remover pergunta antes: a linha é do projeto, e
  // some de toda caixa que tem a mesma posição.
  const [removendo, setRemovendo] = useState<ItemDeReles | null>(null)

  // "Apagar todas": as linhas que a tela lista, de uma vez, com o título da caixa digitado.
  // Vão os ids do que está na tela — a API recusa tudo se a caixa mudou no meio-tempo.
  const [apagandoTodas, setApagandoTodas] = useState(false)
  // "Incluir modelos": o lote aberto com esta caixa como molde (ver `CaixasEmLote`).
  const [incluindoModelos, setIncluindoModelos] = useState(false)
  // "Excluir modelo": esta caixa sai, com as fotos; pergunta antes, logo abaixo do cabeçalho.
  const [excluindoModelo, setExcluindoModelo] = useState(false)
  // "Alterar posições" também sai do menu; a edição fica na seção das posições.
  const [editandoPosicoes, setEditandoPosicoes] = useState(false)
  useEffect(() => {
    setAberto(null); setRemovendo(null); setApagandoTodas(false); setIncluindoModelos(false)
    setExcluindoModelo(false); setEditandoPosicoes(false)
  }, [caixa.id])
  const qc = useQueryClient()
  const apagarTodas = useMutation({
    mutationFn: async (ids: number[]) => {
      await api.post(`${base}/itens/excluir-lote`, { ids }, foraDoProjeto())
      return ids.length
    },
    onSettled: () => { for (const k of invalidar) qc.invalidateQueries({ queryKey: k }) },
  })
  const quantasLinhas = itens.data?.length ?? 0
  const nomeDaCaixa = caixa.titulo?.trim() || `Caixa ${caixa.id}`
  useEffect(() => {
    if (aberto) edicao.current?.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }, [aberto?.id])

  const incluirPosicao = () => {
    if (!novo.identificador.trim() || criar.isPending) return
    aoAvisar(null)
    criar.mutate({ rota: `${base}/itens`, dados: novo }, {
      // A posição fica: quem inclui F01, F02, F03… está na mesma central, e só a
      // designação em diante muda de uma linha para a outra.
      onSuccess: (criada) => {
        setNovo({ ...POSICAO_VAZIA, identificador: novo.identificador })
        aoAvisar("Posição incluída.")
        recente.marcar((criada as ItemDeReles | undefined)?.id)
        designacaoNova.current?.focus()
      },
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  const gravarAberto = () => {
    if (!aberto || alterar.isPending) return
    aoAvisar(null)
    alterar.mutate({ rota: `${base}/itens/${aberto.id}`, dados: aberto }, {
      onSuccess: () => { aoAvisar("Posição gravada."); setAberto(null) },
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  return (
    <Painel>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <b style={{ fontSize: 13.5 }}>{caixa.titulo}</b>
        <span style={{ fontSize: 11, color: "var(--txt3)" }}>
          {nomeOu(nomes.veiculo, caixa.veiculoId, "veículo")}
          {caixa.motorId ? ` · ${nomeOu(nomes.motor, caixa.motorId, "motor")}` : ""}
          {caixa.ano ? ` · ${caixa.ano}` : ""}
        </span>
        <span style={{ marginLeft: "auto" }}>
          {/* O que mexe na caixa fica num menu só; um formulário aberto por vez. */}
          <MenuDeAcoes
            rotulo="Ações da caixa"
            acoes={[
              {
                rotulo: "Incluir modelos",
                dica: "A mesma caixa para outros veículos e anos",
                desligado: posicoes.isPending || imagens.isPending,
                aoEscolher: () => { aoAvisar(null); setExcluindoModelo(false); setEditandoPosicoes(false); setIncluindoModelos(true) },
              },
              {
                rotulo: "Alterar posições",
                dica: "Quais linhas do projeto esta caixa mostra",
                desligado: posicoes.isPending,
                aoEscolher: () => { aoAvisar(null); setExcluindoModelo(false); setIncluindoModelos(false); setEditandoPosicoes(true) },
              },
              {
                rotulo: "Excluir modelo",
                dica: "Tira este veículo e ano da central",
                perigo: true,
                aoEscolher: () => { aoAvisar(null); setIncluindoModelos(false); setEditandoPosicoes(false); setExcluindoModelo(true) },
              },
            ]}
          />
        </span>
        <button onClick={aoFechar} aria-label="Fechar" style={{
          border: 0, background: "transparent", cursor: "pointer",
          color: "var(--txt3)", fontSize: 16, lineHeight: 1,
        }}>×</button>
      </div>

      {excluindoModelo && (
        <div
          role="alert"
          onKeyDown={(e) => { if (e.key === "Escape") setExcluindoModelo(false) }}
          style={{
            display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 12,
            padding: "7px 10px", borderRadius: 6, border: "1px solid var(--erro)",
            background: "color-mix(in srgb, var(--erro) 5%, transparent)", fontSize: 11.5,
          }}
        >
          <span style={{ flex: 1, minWidth: 220, lineHeight: 1.5 }}>
            Excluir o modelo <b>{nomeOu(nomes.veiculo, caixa.veiculoId, "veículo")}{caixa.ano ? ` · ${caixa.ano}` : ""}</b> de{" "}
            <b>{nomeDaCaixa}</b>? Sai esta caixa
            {(imagens.data?.length ?? 0) > 0 && <> com as <b>{imagens.data!.length} foto{imagens.data!.length === 1 ? "" : "s"}</b> dela</>}.
            As linhas da tabela são do projeto e ficam para os outros modelos. Não há como desfazer pela tela.
          </span>
          <Botao
            ocupado={excluir.isPending}
            aoClicar={() => {
              aoAvisar(null)
              excluir.mutate(`api/reles-fusiveis/${projeto.id}/caixas/${caixa.id}`, {
                onSuccess: () => {
                  aoAvisar(`Modelo ${nomeOu(nomes.veiculo, caixa.veiculoId, "veículo")}${caixa.ano ? ` · ${caixa.ano}` : ""} excluído.`)
                  aoFechar()
                },
                onError: (x) => { aoAvisar(mensagem(x)); setExcluindoModelo(false) },
              })
            }}
          >
            Excluir o modelo
          </Botao>
          <Botao aoClicar={() => setExcluindoModelo(false)}>Não</Botao>
        </div>
      )}

      {incluindoModelos && (
        <div style={{ marginBottom: 12 }}>
          <CaixasEmLote
            projetoId={projeto.id}
            nomes={nomes}
            invalidar={[["outros-filhos", "reles-caixas", projeto.id], ["outros", "reles"]]}
            aoAvisar={aoAvisar}
            aPartirDe={{ caixa, posicoes: posicoes.data ?? [], fotos: imagens.data?.length ?? 0 }}
            aoFechar={() => setIncluindoModelos(false)}
          />
        </div>
      )}

      <PosicoesDaCaixa
        projeto={projeto}
        caixa={caixa}
        posicoes={posicoes.data}
        carregando={posicoes.isPending}
        invalidar={invalidar}
        aoAvisar={aoAvisar}
        editando={editandoPosicoes}
        aoMudarEditando={setEditandoPosicoes}
      />

      <Secao
        titulo="O que cada posição alimenta"
        contagem={`${quantasLinhas}`}
        acao={
          <Botao desligado={quantasLinhas === 0 || apagandoTodas}
                 aoClicar={() => { aoAvisar(null); setRemovendo(null); setApagandoTodas(true) }}>
            Apagar todas
          </Botao>
        }
      >
        <p style={{ margin: "-2px 0 7px", fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.5 }}>
          As linhas são do projeto: alterar ou remover vale para toda caixa que tem a mesma posição.
        </p>

        {apagandoTodas && (
          <div style={{ marginBottom: 8 }}>
            <ConfirmacaoDigitada
              alvo={nomeDaCaixa}
              oQueAcontece={
                <>
                  Apaga as <b>{quantasLinhas} linhas</b> desta caixa. Elas são do projeto: saem
                  também de toda caixa com as mesmas posições
                  {(posicoes.data?.length ?? 0) > 0 && <> ({posicoes.data!.join(", ")})</>}. Não há
                  como desfazer pela tela.
                </>
              }
              rotuloDoBotao={`Apagar as ${quantasLinhas} linhas`}
              ocupado={apagarTodas.isPending}
              aoCancelar={() => setApagandoTodas(false)}
              aoConfirmar={() => {
                aoAvisar(null)
                apagarTodas.mutate((itens.data ?? []).map((i) => i.id), {
                  onSuccess: (n) => {
                    aoAvisar(`${n} linha${n === 1 ? "" : "s"} apagada${n === 1 ? "" : "s"}.`)
                    setApagandoTodas(false)
                    setAberto(null)
                  },
                  onError: (e) => { aoAvisar(mensagem(e)); setApagandoTodas(false) },
                })
              }}
            />
          </div>
        )}

        <div ref={tabela}>
        <TabelaSimples
          itens={itens.data ?? []}
          vazio="Esta caixa ainda não tem itens."
          recente={recente.tem}
          selecionado={aberto?.id ?? null}
          aoAbrir={(i) => { aoAvisar(null); setAberto(i) }}
          abaixoDe={(i) => removendo?.id === i.id && (
            <div
              role="alert"
              onKeyDown={(e) => { if (e.key === "Escape") setRemovendo(null) }}
              style={{
                display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8,
                padding: "7px 10px", borderRadius: 6, border: "1px solid var(--erro)",
                background: "color-mix(in srgb, var(--erro) 5%, transparent)", fontSize: 11.5,
                whiteSpace: "normal",
              }}
            >
              <span style={{ flex: 1, minWidth: 220, lineHeight: 1.5 }}>
                Remover a linha <b>{i.designacao || i.identificador}</b>
                {i.funcao ? ` (${i.funcao})` : ""}? Ela sai de toda caixa deste projeto com a
                posição <b>{i.identificador}</b>.
              </span>
              <Botao
                ocupado={excluir.isPending}
                aoClicar={() => {
                  aoAvisar(null)
                  excluir.mutate(`${base}/itens/${i.id}`, {
                    onSuccess: () => {
                      aoAvisar("Item removido.")
                      setRemovendo(null)
                      setAberto((a) => (a?.id === i.id ? null : a))
                    },
                    onError: (x) => { aoAvisar(mensagem(x)); setRemovendo(null) },
                  })
                }}
              >
                Remover a linha
              </Botao>
              <Botao aoClicar={() => setRemovendo(null)}>Não</Botao>
            </div>
          )}
          colunas={[
            { chave: "identificador", rotulo: "Posição", largura: 90 },
            { chave: "designacao", rotulo: "Designação", largura: 110 },
            { chave: "corrente", rotulo: "Corrente", largura: 90 },
            { chave: "funcao", rotulo: "Função" },
            { chave: "componente", rotulo: "Componente", largura: 170 },
            { chave: "local", rotulo: "Local", largura: 120 },
            {
              chave: "id", rotulo: "", largura: 50,
              render: (i) => (
                <button
                  aria-label={`Remover ${i.designacao || i.identificador}`}
                  onClick={(e) => {
                    // A linha inteira abre a edição; o × não pode abrir junto.
                    e.stopPropagation()
                    aoAvisar(null)
                    setRemovendo(i)
                  }}
                  style={{
                    border: 0, background: "transparent", cursor: "pointer",
                    color: "var(--txt3)", fontSize: 13, lineHeight: 1,
                  }}
                >
                  ×
                </button>
              ),
            },
          ]}
        />
        </div>
      </Secao>

      {aberto && (
        <div ref={edicao}>
          <Secao titulo={`Alterar posição ${[aberto.identificador, aberto.designacao].filter(Boolean).join(" · ")}`}>
            <form
              onSubmit={(e) => { e.preventDefault(); gravarAberto() }}
              onKeyDown={(e) => { if (e.key === "Escape") setAberto(null) }}
            >
              <CamposDaPosicao valor={aberto} aoMudar={(p) => setAberto({ ...aberto, ...p })} />
              <div style={{ display: "flex", gap: 6 }}>
                <Botao destaque envia ocupado={alterar.isPending} aoClicar={() => {}}>
                  {alterar.isPending ? "Gravando…" : "Gravar"}
                </Botao>
                <Botao aoClicar={() => setAberto(null)}>Cancelar</Botao>
              </div>
            </form>
          </Secao>
        </div>
      )}

      {emLote ? (
        <Secao titulo="Incluir em lote (.txt)">
          <LoteDePosicoes
            rota={`${base}/itens/lote`}
            posicaoInicial={novo.identificador}
            invalidar={invalidar}
            aoAvisar={aoAvisar}
            aoIncluir={(criadas) => {
              setEmLote(false)
              rolarAteNovas.current = true
              recente.marcar(criadas.map((c) => c.id))
            }}
            aoFechar={() => setEmLote(false)}
          />
        </Secao>
      ) : (
      <Secao
        titulo="Incluir posição"
        acao={<Botao aoClicar={() => { aoAvisar(null); setEmLote(true) }}>Incluir em lote (.txt)</Botao>}
      >
        <form onSubmit={(e) => { e.preventDefault(); incluirPosicao() }}>
          <CamposDaPosicao valor={novo} aoMudar={(p) => setNovo({ ...novo, ...p })}
                           designacao={designacaoNova} />
          <Botao
            destaque
            envia
            desligado={!novo.identificador.trim()}
            ocupado={criar.isPending}
            aoClicar={() => {}}
          >
            Incluir
          </Botao>
        </form>
      </Secao>
      )}

      <Secao titulo="Imagens da caixa" contagem={`${imagens.data?.length ?? 0}`}>
        <ImagensOrdenaveis
          base={`${base}/imagens`}
          itens={imagens.data ?? []}
          invalidar={invalidar}
          aoAvisar={aoAvisar}
        />
        <input
          type="file"
          accept="image/*"
          style={{ fontSize: 11, marginTop: 7 }}
          onChange={(e) => {
            const arquivo = e.target.files?.[0]
            if (!arquivo) return
            aoAvisar(null)
            // A nova vai para o fim da lista.
            const ordem = Math.max(0, ...(imagens.data ?? []).map((x) => x.ordem ?? 0)) + 1
            subir.mutate({ rota: `${base}/imagens`, arquivo, campo: "Imagem", extras: { Ordem: String(ordem) } }, {
              onSuccess: () => aoAvisar(`${arquivo.name} enviada.`),
              onError: (x) => aoAvisar(mensagem(x)),
            })
            e.target.value = ""
          }}
        />
        {subir.isPending && (
          <span style={{ fontSize: 11, color: "var(--txt2)", marginLeft: 8 }}>enviando…</span>
        )}
      </Secao>
    </Painel>
  )
}

/**
 * As posições da caixa — o que liga a caixa às linhas do projeto.
 *
 * A caixa mostra as linhas do projeto cuja posição está aqui. Era isso que faltava:
 * a API devolvia o projeto inteiro e o Fox 2005 e o Fox 2012 apareciam iguais. Trocar
 * as posições é o `PUT` da caixa, com o resto dela como está.
 */
function PosicoesDaCaixa({ projeto, caixa, posicoes, carregando, invalidar, aoAvisar, editando, aoMudarEditando: setEditando }: {
  projeto: ProjetoDeReles
  caixa: CaixaDeReles
  posicoes: string[] | undefined
  carregando: boolean
  invalidar: (string | number)[][]
  aoAvisar: (m: string | null) => void
  /** Quem abre a edição é o menu "Ações da caixa", no cabeçalho. */
  editando: boolean
  aoMudarEditando: (v: boolean) => void
}) {
  const { alterar } = usarGravar(invalidar)
  const [texto, setTexto] = useState("")
  const lidas = useMemo(() => lerPosicoes(texto), [texto])

  // Abrir a edição começa do que a caixa tem hoje.
  useEffect(() => { if (editando) setTexto((posicoes ?? []).join(", ")) }, [editando])

  const gravar = () => {
    aoAvisar(null)
    alterar.mutate({
      rota: `api/reles-fusiveis/${projeto.id}/caixas/${caixa.id}`,
      dados: { ...caixa, versao: caixa.versao ?? "", identificadores: lidas.join(",") },
    }, {
      onSuccess: () => { aoAvisar(`Posições da caixa gravadas (${lidas.length}).`); setEditando(false) },
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  return (
    <Secao
      titulo="Posições desta caixa"
      contagem={carregando ? "…" : `${posicoes?.length ?? 0}`}
    >
      {editando ? (
        <form onSubmit={(e) => { e.preventDefault(); gravar() }}
              onKeyDown={(e) => { if (e.key === "Escape") setEditando(false) }}>
          <Campo rotulo="Posições"
                 ajuda="Separadas por vírgula ou uma por linha. A caixa mostra as linhas do projeto com estas posições.">
            <textarea autoFocus value={texto} rows={3} onChange={(e) => setTexto(e.target.value)}
                      style={{ ...estiloCampo, resize: "vertical", fontFamily: "inherit" }} />
          </Campo>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Botao destaque envia ocupado={alterar.isPending} aoClicar={() => {}}>
              {alterar.isPending ? "Gravando…" : `Gravar ${lidas.length} posiç${lidas.length === 1 ? "ão" : "ões"}`}
            </Botao>
            <Botao aoClicar={() => setEditando(false)}>Cancelar</Botao>
          </div>
        </form>
      ) : carregando ? (
        <p style={{ margin: 0, fontSize: 11.5, color: "var(--txt2)" }}>Carregando…</p>
      ) : (posicoes?.length ?? 0) === 0 ? (
        <p style={{ margin: 0, fontSize: 11.5, color: "var(--txt3)" }}>
          Esta caixa não tem posição — por isso não mostra linha nenhuma. Inclua uma posição
          abaixo ou altere as posições.
        </p>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {posicoes!.map((p) => <Etiqueta key={p}>{p}</Etiqueta>)}
        </div>
      )}
    </Secao>
  )
}

type Posicao = Omit<ItemDeReles, "id">

const POSICAO_VAZIA: Posicao = {
  identificador: "", designacao: "", local: "", definicao: "", corrente: "",
  funcao: "", componente: "", terminal: "", cor: "", restricao: "",
}

/**
 * Os campos de uma posição — os mesmos para incluir e para alterar.
 *
 * `definicao` e `restricao` não aparecem, como já não apareciam na inclusão; na
 * alteração eles vão no corpo do PUT como vieram, porque o PUT troca a linha inteira.
 */
function CamposDaPosicao({ valor, aoMudar, designacao }: {
  valor: Posicao
  aoMudar: (p: Partial<Posicao>) => void
  designacao?: React.Ref<HTMLInputElement>
}) {
  return (
    <>
      <Linha>
        <Campo rotulo="Posição" ajuda="F19, R3…">
          <input value={valor.identificador} maxLength={TETO_DO_ITEM_DE_RELES.identificador} style={estiloCampo}
                 onChange={(e) => aoMudar({ identificador: e.target.value })} />
        </Campo>
        <Campo rotulo="Designação">
          <input ref={designacao} value={valor.designacao} maxLength={TETO_DO_ITEM_DE_RELES.designacao} style={estiloCampo}
                 onChange={(e) => aoMudar({ designacao: e.target.value })} />
        </Campo>
        <Campo rotulo="Corrente">
          <input value={valor.corrente} maxLength={TETO_DO_ITEM_DE_RELES.corrente} style={estiloCampo} placeholder="10A"
                 onChange={(e) => aoMudar({ corrente: e.target.value })} />
        </Campo>
      </Linha>
      <Linha>
        <Campo rotulo="Função">
          <input value={valor.funcao} maxLength={TETO_DO_ITEM_DE_RELES.funcao} style={estiloCampo}
                 onChange={(e) => aoMudar({ funcao: e.target.value })} />
        </Campo>
        <Campo rotulo="Componente">
          <input value={valor.componente} maxLength={TETO_DO_ITEM_DE_RELES.componente} style={estiloCampo}
                 onChange={(e) => aoMudar({ componente: e.target.value })} />
        </Campo>
      </Linha>
      <Linha>
        <Campo rotulo="Local">
          <input value={valor.local} maxLength={TETO_DO_ITEM_DE_RELES.local} style={estiloCampo}
                 onChange={(e) => aoMudar({ local: e.target.value })} />
        </Campo>
        <Campo rotulo="Terminal">
          <input value={valor.terminal} maxLength={TETO_DO_ITEM_DE_RELES.terminal} style={estiloCampo}
                 onChange={(e) => aoMudar({ terminal: e.target.value })} />
        </Campo>
        <Campo rotulo="Cor">
          <input value={valor.cor} maxLength={TETO_DO_ITEM_DE_RELES.cor} style={estiloCampo}
                 onChange={(e) => aoMudar({ cor: e.target.value })} />
        </Campo>
      </Linha>
    </>
  )
}

/** Uma imagem da caixa, como a API lista: `imagem` é o nome do arquivo na ApiConteudo. */
type ImagemDaCaixa = { id: number; imagem?: string; ordem?: number }

/**
 * A lista de imagens da caixa, com miniatura para conferir, a ordem editável e o ×
 * para apagar — este pergunta antes, logo abaixo da imagem, como o × das linhas.
 *
 * A foto vem pela ApiSistema (`.../imagens/{id}/arquivo`), que a lê da ApiConteudo:
 * a rota exige token, e `<img src>` não manda token — por isso o blob. Clicar na
 * miniatura abre a foto grande, com as outras da caixa nas setas.
 *
 * **Reordenar é metadado.** A ordem é uma coluna; o arquivo continua onde está.
 * O corpo é a lista inteira com o número novo de cada uma, e não só o par que
 * trocou: mandar só a diferença deixaria buraco na numeração se alguém tivesse
 * mexido no meio-tempo.
 */
function ImagensOrdenaveis({ base, itens, invalidar, aoAvisar }: {
  /** `.../caixas/{caixaId}/imagens` */
  base: string
  itens: ImagemDaCaixa[]
  invalidar: (string | number)[][]
  aoAvisar: (m: string | null) => void
}) {
  const qc = useQueryClient()
  const [aberta, setAberta] = useState<number | null>(null)

  // Estável por caixa: o `usarImagem` baixa de novo quando ela muda.
  const carregar = useCallback<CarregarImagem>(async (id, largura) => {
    const r = await api.get<Blob>(`${base}/${id}/arquivo`, {
      ...foraDoProjeto(), params: { largura }, responseType: "blob",
    })
    return URL.createObjectURL(r.data)
  }, [base])

  const ordenar = useMutation({
    mutationFn: async (imagens: { id: number; ordem: number }[]) => {
      await api.put(`${base}/ordem`, { imagens }, foraDoProjeto())
    },
    onSuccess: () => { for (const k of invalidar) qc.invalidateQueries({ queryKey: k }) },
    onError: (e) => aoAvisar(mensagem(e)),
  })

  // A imagem que o × marcou para apagar. Pergunta antes: apagar tira a linha e o
  // arquivo do servidor, e não há como desfazer pela tela.
  const [apagando, setApagando] = useState<ImagemDaCaixa | null>(null)
  const apagar = useMutation({
    mutationFn: async (id: number) => { await api.delete(`${base}/${id}`, foraDoProjeto()) },
    onSettled: () => { for (const k of invalidar) qc.invalidateQueries({ queryKey: k }) },
  })

  function mover(indice: number, passo: -1 | 1) {
    const destino = indice + passo
    if (destino < 0 || destino >= itens.length) return

    const ids = itens.map((x) => x.id)
    const t = ids[indice]!
    ids[indice] = ids[destino]!
    ids[destino] = t

    aoAvisar(null)
    ordenar.mutate(ids.map((id, i) => ({ id, ordem: i + 1 })))
  }

  if (itens.length === 0) {
    return <p style={{ margin: 0, fontSize: 11.5, color: "var(--txt3)" }}>Sem imagem.</p>
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {itens.map((im, i) => {
        const nome = (im.imagem ?? "").split(/[\\/]/).pop() || `#${im.id}`
        return (
          <div key={im.id} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ display: "flex", gap: 8, fontSize: 11, alignItems: "center" }}>
              <span className="num" style={{ flex: "0 0 26px", color: "var(--txt3)" }}>{im.ordem ?? i + 1}</span>
              <MiniaturaDaCaixa id={im.id} carregar={carregar} aoAbrir={() => setAberta(i)} />
              <span className="mono" style={{
                flex: 1, minWidth: 0, color: "var(--txt2)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {nome}
              </span>
              <button onClick={() => mover(i, -1)} disabled={i === 0 || ordenar.isPending}
                      aria-label="Subir" style={botaoDeOrdem}>↑</button>
              <button onClick={() => mover(i, 1)} disabled={i === itens.length - 1 || ordenar.isPending}
                      aria-label="Descer" style={botaoDeOrdem}>↓</button>
              <button onClick={() => { aoAvisar(null); setApagando(im) }} disabled={apagar.isPending}
                      aria-label={`Apagar ${nome}`} title="Apagar a imagem"
                      style={{ ...botaoDeOrdem, fontSize: 12 }}>×</button>
            </span>
            {apagando?.id === im.id && (
              <div
                role="alert"
                onKeyDown={(e) => { if (e.key === "Escape") setApagando(null) }}
                style={{
                  display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8,
                  marginLeft: 34, padding: "7px 10px", borderRadius: 6, border: "1px solid var(--erro)",
                  background: "color-mix(in srgb, var(--erro) 5%, transparent)", fontSize: 11.5,
                }}
              >
                <span style={{ flex: 1, minWidth: 220, lineHeight: 1.5 }}>
                  Apagar a imagem <b>{nome}</b>? Ela sai desta caixa e o arquivo é apagado do
                  servidor — não há como desfazer pela tela.
                </span>
                <Botao
                  ocupado={apagar.isPending}
                  aoClicar={() => {
                    aoAvisar(null)
                    apagar.mutate(im.id, {
                      onSuccess: () => { aoAvisar(`Imagem ${nome} apagada.`); setApagando(null) },
                      onError: (x) => { aoAvisar(mensagem(x)); setApagando(null) },
                    })
                  }}
                >
                  Apagar a imagem
                </Botao>
                <Botao aoClicar={() => setApagando(null)}>Não</Botao>
              </div>
            )}
          </div>
        )
      })}
      {aberta != null && (
        <Galeria
          ids={itens.map((x) => x.id)}
          inicial={aberta}
          aoFechar={() => setAberta(null)}
          carregar={carregar}
          rotulo="Imagem da caixa"
          prefixo="caixa-imagem"
        />
      )}
    </div>
  )
}

/** A miniatura de uma imagem da caixa; sem arquivo no servidor, avisa em vez de abrir. */
function MiniaturaDaCaixa({ id, carregar, aoAbrir }: { id: number; carregar: CarregarImagem; aoAbrir: () => void }) {
  const { url, falhou } = usarImagem(id, 160, carregar)
  return (
    <button
      type="button"
      onClick={aoAbrir}
      disabled={falhou}
      title={falhou ? "A foto não foi encontrada no servidor de arquivos" : "Ampliar"}
      style={{
        flex: "0 0 auto", width: 64, height: 46, padding: 0, borderRadius: 6, overflow: "hidden",
        border: "1px solid var(--line)", background: "var(--surface3)", color: "var(--txt3)",
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9,
        cursor: falhou ? "default" : "zoom-in",
      }}
    >
      {url ? (
        <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : falhou ? "sem arquivo" : "…"}
    </button>
  )
}

const botaoDeOrdem: React.CSSProperties = {
  border: "1px solid var(--line)", borderRadius: 4, background: "transparent",
  color: "var(--txt2)", cursor: "pointer", fontSize: 10, lineHeight: 1,
  padding: "1px 5px",
}
